import React, { useState } from 'react';
import { 
  Box, 
  Button, 
  Container, 
  Flex, 
  Heading, 
  Text, 
  VStack,
  HStack,
  Icon,
  useColorModeValue,
  SimpleGrid,
  Link,
  useBreakpointValue,
  Image
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { FaPlay, FaTheaterMasks, FaArrowRight, FaGamepad } from 'react-icons/fa';
import AppHeader from '../components/AppHeader';
import { useMusicService } from '../context/MusicServiceContext';
// Import images from assets
import playerPreview from '../assets/images/player-preview.jpg';
import theatrePreview from '../assets/images/theatre-preview.jpg';
import arcadePreview from '../assets/images/arcade-preview.jpg';
const Home = () => {
  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');
  const isMobile = useBreakpointValue({ base: true, md: false });
  
  // Add music service and layout state to match Player page
  // These variables are used by the AppHeader component indirectly
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { 
    currentService, 
    setCurrentService,
    isSpotifySelected,
    isAppleSelected
  } = useMusicService();
  
  // Layout state (just like in Player)
  const [layoutMode, setLayoutMode] = useState<'split' | 'tesla'>('split');

  const sections = [
    {
      title: "Player",
      description: "Experience your music with synchronized lyrics in a sleek, modern interface. Perfect for individual listening sessions.",
      icon: FaPlay,
      path: "/player",
      gradient: "linear(to-br, #1DB954, #1ed760)",
      image: playerPreview
    },
    {
      title: "Theatre",
      description: "Transform your space into a premium entertainment hub. Access all your favorite streaming services in one place.",
      icon: FaTheaterMasks,
      path: "/theatre-apps",
      gradient: "linear(to-br, #FF4D4D, #FF6B6B)",
      image: theatrePreview
    },
    {
      title: "Arcade",
      description: "Play your favorite games in a sleek, modern interface. Perfect for individual listening sessions.",
      icon: FaGamepad,
      path: "/arcade",
      gradient: "linear(to-br,rgb(103, 95, 243), #FF6B6B)",
      image: arcadePreview
    }
  ];

  return (
    <Box 
      minH="100vh"
      bgGradient="linear(to-b, gray.900, black)"
      color="white"
      position="relative"
      overflow="hidden"
    >
      {/* Header */}
      <AppHeader 
        isCondensed={true}
        accentColor="#1DB954"
        layoutMode={layoutMode}
        setLayoutMode={setLayoutMode}
      />

      {/* Background Effects */}
      <Box
        position="absolute"
        top="0"
        left="0"
        right="0"
        bottom="0"
        bgGradient="radial(circle at 20% 20%, rgba(29, 185, 84, 0.1), transparent 40%)"
        pointerEvents="none"
      />
      <Box
        position="absolute"
        top="0"
        left="0"
        right="0"
        bottom="0"
        bgGradient="radial(circle at 80% 80%, rgba(255, 77, 77, 0.1), transparent 40%)"
        pointerEvents="none"
      />

      <Container maxW="container.xl" py={20} position="relative" mt="72px">
        <VStack spacing={16} align="stretch">
          {/* Header Section */}
          <VStack spacing={6} textAlign="center">
            <Heading
              fontSize={{ base: "4xl", md: "6xl" }}
              fontWeight="extrabold"
              bg="white"
              bgClip="text"
              letterSpacing="tight"
            >
              Welcome to SyncdIt
            </Heading>
            <Text
              fontSize={{ base: "lg", md: "xl" }}
              color="gray.400"
              maxW="2xl"
              mx="auto"
            >
              Choose your experience. Whether you're looking for synchronized lyrics or a complete entertainment hub, we've got you covered.
            </Text>
          </VStack>

          {/* Main Sections */}
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={12}>
            {sections.map((section) => (
              <Box
                key={section.title}
                as={RouterLink}
                to={section.path}
                position="relative"
                overflow="hidden"
                borderRadius="2xl"
                transition="all 0.3s ease"
                height="auto"
                boxShadow="0 10px 30px rgba(0,0,0,0.5)"
                _hover={{
                  transform: "translateY(-5px)",
                  boxShadow: "0 15px 40px rgba(0,0,0,0.6)"
                }}
                className="glass-card"
                display="flex"
                flexDirection="column"
              >
                {/* Preview Image (Top Half) */}
                <Box
                  flex="3"
                  width="100%"
                  position="relative"
                  overflow="hidden"
                  borderTopRadius="xl"
                >
                  <Image 
                    src={section.image} 
                    alt={`${section.title} preview`} 
                    objectFit="cover"
                    width="100%"
                    height="100%"
                  />
                </Box>

                {/* Content Section (Bottom Half) */}
                <Box
                  flex="2"
                  p={6}
                  display="flex"
                  flexDirection="column"
                  justifyContent="space-between"
                  bg="rgba(0,0,0,0.7)"
                  borderBottomRadius="xl"
                  backdropFilter="blur(10px)"
                >
                  {/* Top Section */}
                  <VStack align="start" spacing={4}>
                    <HStack spacing={4}>
                      <Box
                        p={3}
                        borderRadius="xl"
                        bgGradient={section.gradient}
                        boxShadow="0 0 20px rgba(0,0,0,0.3)"
                      >
                        <Icon as={section.icon} boxSize={6} color="white" />
                      </Box>
                      <Heading size="lg">{section.title}</Heading>
                    </HStack>
                    <Text color="gray.200" fontSize="md" fontWeight="medium">
                      {section.description}
                    </Text>
                  </VStack>

                  {/* Bottom Section */}
                  <Button 
                    rightIcon={<FaArrowRight />}
                    variant="solid"
                    bg={section.title === "Player" ? "green.500" : "red.500"}
                    color="white"
                    size="md"
                    _hover={{ 
                      bg: section.title === "Player" ? "green.600" : "red.600",
                      transform: "translateX(5px)"
                    }}
                    mt={4}
                  >
                    Enter {section.title}
                  </Button>
                </Box>
              </Box>
            ))}
          </SimpleGrid>
        </VStack>
      </Container>
    </Box>
  );
};

export default Home; 